import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
 
interface NavItem {
  label: string;
  to: string;
  external?: boolean;
  isCta?: boolean;
}
 
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
 
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
 
  // Close menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);
 
  const navItems: NavItem[] = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About' },
    { to: '/case-studies', label: 'Case Studies' },
    { to: '/contact', label: 'Contact', isCta: true },
  ];
 
  const isActive = (path: string) => location.pathname === path;
 
  const navLinkBase = 'text-sm tracking-wide transition-colors duration-300 whitespace-nowrap';
  const navLinkActive = 'text-black font-semibold';
  const navLinkInactive = 'text-gray-600 hover:text-black';
  const ctaBase =
    'inline-flex items-center gap-2 px-5 py-2.5 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap';
 
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-gray-200'
          : 'bg-transparent'
      }`}
    >
      {/* HEADER */}
      <div className="w-full px-6 md:px-10 lg:px-16 bg-white">
        <div className="flex items-center justify-between h-16 md:h-20">
         
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src= "/assets/azosis logo.webp"
              alt="Azosis Technologies"
              className="h-8 md:h-10 object-contain"
            />
          </Link>
 
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navItems.map((item) => {
              if (item.isCta) {
                return item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={ctaBase}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link key={item.to} to={item.to} className={ctaBase}>
                    {item.label}
                  </Link>
                );
              }
 
              return item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${navLinkBase} ${navLinkInactive}`}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`${navLinkBase} ${
                    isActive(item.to) ? navLinkActive : navLinkInactive
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
 
          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>
 
      {/* MOBILE MENU (FULL FIXED) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-white flex flex-col h-screen overflow-y-auto">
 
          {/* Mobile Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <img
              src="/assets/azosis logo.webp"
              alt="Azosis Technologies"
              className="h-8"
            />
 
            <button onClick={() => setMobileOpen(false)}>
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
 
          {/* Mobile Content */}
          <div className="flex flex-col px-6 py-6">
 
            {/* Links */}
            <div className="space-y-6 mt-6">
              {navItems.map((item) => {
                if (item.isCta) return null;
 
                return item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="block text-lg text-gray-700"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`block text-lg ${
                      isActive(item.to)
                        ? 'text-azosis-gold font-semibold'
                        : 'text-gray-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
 
            {/* Buttons */}
            <div className="mt-6 space-y-6">
    <Link
  to="/contact"
  className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
>
  Contact
</Link>
 
 
</div>
 
          </div>
        </div>
      )}
    </nav>
  );
}